import dayjs from "dayjs";
import { PointOfSaleTransaction } from "../../../model/pointofsale/PointOfSaleTransaction";

const INCOMPLETE_STATUSES = ["Unpaid", "Pending", "Failed"];

// A membership payment taken at the till that did not go through. The member has
// not paid for their membership, so the profile treats it as red (FORT-454) rather
// than lumping it in with the amber "incomplete payments" warning.
const FAILED_MEMBERSHIP_STATUSES = ["Unpaid", "Failed"];

// A membership charge still waiting to be paid — the manual-payment rollover raises
// one per member as Pending, and extends valid_to before it is paid. The member owes
// for their membership, so this is red too (FORT-455).
const OUTSTANDING_MEMBERSHIP_STATUSES = ["Pending"];

// The point-of-sale item the manual-payment rollover charges against
// (MemberService.MembershipSubscriptionItem). Matched by name as well as by type,
// because each club configures the item's type itself.
const MEMBERSHIP_SUBSCRIPTION_ITEM = "Membership Subscription";

const isMembershipTransaction = (transaction: PointOfSaleTransaction): boolean =>
    (transaction.items ?? []).some(
        (line) => line.item?.type === "Membership" || line.item?.name === MEMBERSHIP_SUBSCRIPTION_ITEM,
    );

// Not gated on booking slots (unlike hasIncompletePosPayments): a membership
// payment is owed as soon as it fails, whatever else the transaction carries.
export const isFailedMembershipPayment = (transaction: PointOfSaleTransaction): boolean =>
    FAILED_MEMBERSHIP_STATUSES.includes(transaction.status) && isMembershipTransaction(transaction);

export const hasFailedMembershipPayments = (
    transactions: PointOfSaleTransaction[] | undefined
): boolean => (transactions ?? []).some(isFailedMembershipPayment);

export const isOutstandingMembershipPayment = (transaction: PointOfSaleTransaction): boolean =>
    OUTSTANDING_MEMBERSHIP_STATUSES.includes(transaction.status) && isMembershipTransaction(transaction);

export const hasOutstandingMembershipPayments = (
    transactions: PointOfSaleTransaction[] | undefined
): boolean => (transactions ?? []).some(isOutstandingMembershipPayment);

export const hasIncompletePosPayments = (
    transactions: PointOfSaleTransaction[] | undefined,
    now: dayjs.Dayjs = dayjs()
): boolean => {
    return (transactions ?? []).some((transaction) => {
        if (!INCOMPLETE_STATUSES.includes(transaction.status)) {
            return false;
        }
        const bookingSlots = transaction.bookingSlots ?? [];
        if (bookingSlots.length === 0) {
            return true;
        }
        return bookingSlots.some(({ bookingSlot }) =>
            dayjs(bookingSlot.endTime).isBefore(now)
        );
    });
};
